import { useContext, useRef, useState } from "react"
import { Navigate } from "react-router-dom"
import { api, goToLogin, postDeleteAccount } from "../api/client"
import { UserContext } from "../components/UserContext"
import { useNotify } from "../components/notice/useNotify"
import { Camera } from "lucide-react"
import LocationSettings from "../components/account/LocationSettings"
import TimestampDisplaySettings from "../components/account/TimestampDisplaySettings"
import ConfirmationModal from "../components/ConfirmationModal"

// The phrase the server checks too: exact and case-sensitive.
const DELETE_PHRASE = "DELETE"
import PushSettings from "../components/account/PushSettings"

const AccountPage = () => {
    const { user, refreshUser } = useContext(UserContext)
    const notify = useNotify()
    const [isUploading, setIsUploading] = useState(false)
    const fileInputRef = useRef(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deletePhrase, setDeletePhrase] = useState("")
    const [deleteError, setDeleteError] = useState("")
    const [isDeleting, setIsDeleting] = useState(false)

    const handlePhotoUpload = async (event) => {
        const file = event.target.files[0]
        if (!file) return

        const formData = new FormData()
        formData.append("profile_image", file)
        
        setIsUploading(true)
        try {
            await api.post("/api/update-profile-pic", formData)
            await refreshUser()
        } catch (error) {
            console.error("Failed to upload:", error)
            notify.error("Failed to upload photo. Please try again.")
        } finally {
            setIsUploading(false)
        }
    }

    const phraseMatches = deletePhrase === DELETE_PHRASE

    const openDeleteModal = () => {
        setDeletePhrase("")
        setDeleteError("")
        setShowDeleteModal(true)
    }

    const handleDeleteAccount = async () => {
        if (!phraseMatches || isDeleting) return
        setDeleteError("")
        setIsDeleting(true)
        try {
            await postDeleteAccount(deletePhrase)
            // A full page load, like logout, so no signed-in page stays
            // mounted. `isDeleting` stays set until the page is gone.
            goToLogin("?account_deleted=true")
        } catch (error) {
            console.error("Failed to delete account:", error)
            setDeleteError("Couldn't delete your account. Try again.")
            setIsDeleting(false)
        }
    }

    if (!user) {
        return <Navigate to="/" />
    }

    return (
        <div className="max-w-lg mx-auto bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Account</h2>
            
            <div className="flex flex-col items-center mb-4">
                <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                    <img
                        src={user.profilePicURL || "/static/images/default_avatar.jpg"}
                        className="w-36 h-36 rounded-full object-cover border-2 border-gray-300"
                        alt="Profile"
                    />
                    <div className="absolute bottom-0 right-0 bg-action p-2 rounded-full">
                        <Camera size={20} className="text-white" />
                    </div>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    capture="user"
                    onChange={handlePhotoUpload}
                />
                {isUploading && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
            </div>

            <div className="mb-3">
                <label className="text-sm text-gray-600">First Name</label>
                <p className="text-lg font-medium">{user.firstName}</p>
            </div>

            <div className="mb-3">
                <label className="text-sm text-gray-600">Last Name</label>
                <p className="text-lg font-medium">{user.lastName}</p>
            </div>

            <div className="mb-3">
                <label className="text-sm text-gray-600">Email</label>
                <p className="text-lg font-medium">{user.email}</p>
            </div>

            <TimestampDisplaySettings />

            <LocationSettings />

            <PushSettings />

            <div className="mt-8 pt-6 border-t border-gray-200">
                <h2 className="text-lg font-semibold mb-1">Delete account</h2>
                {user.isOwner ? (
                    <p className="text-sm text-gray-600">The owner account can't be deleted.</p>
                ) : (
                    <>
                        <p className="text-sm text-gray-600 mb-4">
                            Permanently delete your checkins, projects, collections, advisors, chats and photos.
                        </p>
                        <button
                            className="py-2 px-4 bg-danger text-white rounded-md hover:bg-danger-dark disabled:opacity-50"
                            onClick={openDeleteModal}
                        >
                            Delete account
                        </button>
                    </>
                )}
            </div>

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteAccount}
                title="Delete your account?"
                message="This can't be undone."
                confirmText="Delete account"
                confirmDisabled={!phraseMatches}
                isLoading={isDeleting}
            >
                <label className="block mt-4 text-sm text-gray-600" htmlFor="delete-account-phrase">
                    Type {DELETE_PHRASE} to confirm
                </label>
                <input
                    id="delete-account-phrase"
                    type="text"
                    value={deletePhrase}
                    onChange={(e) => setDeletePhrase(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleDeleteAccount()
                    }}
                    placeholder={DELETE_PHRASE}
                    disabled={isDeleting}
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                />
                {deleteError && <p className="mt-2 text-sm text-danger">{deleteError}</p>}
            </ConfirmationModal>
        </div>
    )
}

export default AccountPage